import React, { useState, useCallback, useMemo } from 'react';

import { ResponsiveBar } from '@nivo/bar';
import { fetchGogglesSWAdoption } from '../services/reportsService';
import { useError } from '../contexts/ErrorContext';
import { getHardwareModel } from '../utils/hardware';
import { VERSION_NOTES } from '../constants/versionNotes';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import './GogglesSWAdoptionReport.css';

const compareVersions = (a, b) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na !== nb) return na - nb;
  }
  return 0;
};

const INSTALL_BASE_COLORS = [
  '#0a84ff', '#30d158', '#ff9f0a', '#bf5af2',
  '#ff375f', '#64d2ff', '#ffd60a', '#ff6482',
];

const chartTheme = {
  background: 'transparent',
  textColor: '#ffffff',
  fontSize: 12,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', sans-serif",
  axis: {
    domain: { line: { stroke: '#444444' } },
    ticks: { line: { stroke: '#444444' }, text: { fill: '#999999' } },
    legend: { text: { fill: '#cccccc' } }
  },
  grid: { line: { stroke: '#333333' } },
  legends: { text: { fill: '#999999' } },
  tooltip: {
    container: {
      background: '#1a1a1a', color: '#ffffff', fontSize: 12,
      borderRadius: 8, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)'
    }
  }
};

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const GogglesSWAdoptionReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedVersion(null);
    try {
      const result = await fetchGogglesSWAdoption(startDate, endDate, addError);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, addError]);

  // A row with no uid has no user page to open
  const handleUserClick = useCallback((e, user) => {
    if (!user.user_id) e.preventDefault();
  }, []);

  const pivotAdoptionData = useCallback((rows, valueKey) => {
    if (!rows || rows.length === 0) return { chartData: [], keys: [] };
    const namedRows = rows.map(r => ({
      ...r,
      hw_model: getHardwareModel(r.hw_model) || r.hw_model,
    }));
    const hwModels = [...new Set(namedRows.map(r => r.hw_model))].sort();
    const byVersion = {};
    for (const row of namedRows) {
      if (!byVersion[row.sw_version]) {
        byVersion[row.sw_version] = { sw_version: row.sw_version };
        for (const hw of hwModels) byVersion[row.sw_version][hw] = 0;
      }
      byVersion[row.sw_version][row.hw_model] = row[valueKey];
    }
    const sorted = Object.values(byVersion).sort((a, b) => compareVersions(a.sw_version, b.sw_version));
    return { chartData: sorted, keys: hwModels };
  }, []);

  const { chartData: installBaseChartData, keys: installBaseKeys } = useMemo(
    () => pivotAdoptionData(data?.installBase, 'unique_users'),
    [data?.installBase, pivotAdoptionData]
  );
  const { chartData: uniqueDaysChartData, keys: uniqueDaysKeys } = useMemo(
    () => pivotAdoptionData(data?.uniqueDays, 'unique_days'),
    [data?.uniqueDays, pivotAdoptionData]
  );

  // Per-version summary rows for the left table
  const versionSummary = useMemo(() => {
    if (!data?.userUniqueDays) return [];
    const map = {};
    for (const row of data.userUniqueDays) {
      if (!map[row.sw_version]) {
        map[row.sw_version] = { sw_version: row.sw_version, user_count: 0, total_days: 0 };
      }
      map[row.sw_version].user_count += 1;
      map[row.sw_version].total_days += row.unique_days;
    }
    return Object.values(map).sort((a, b) => compareVersions(a.sw_version, b.sw_version));
  }, [data]);

  // Users on the selected version, sorted by unique_days desc
  const usersForVersion = useMemo(() => {
    if (!selectedVersion || !data?.userUniqueDays) return [];
    return data.userUniqueDays
      .filter(r => r.sw_version === selectedVersion)
      .sort((a, b) => b.unique_days - a.unique_days);
  }, [selectedVersion, data]);

  return (
    <div className="gsa-report">
      <div className="gsa-header">
        <div className="gsa-header-left">
          <div className="gsa-title-section">
            <h2 className="gsa-title">Goggles SW Adoption</h2>
            <p className="gsa-subtitle">Firmware install base and ski-day usage per version</p>
          </div>
        </div>
        <div className="gsa-controls">
          <input
            type="date"
            className="gsa-date-input"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <span className="gsa-date-sep">to</span>
          <input
            type="date"
            className="gsa-date-input"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
          <button className="gsa-fetch-btn" onClick={handleFetch} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {/* Version notes bulletin */}
      <div className="gsa-bulletin">
        <button className="gsa-bulletin-toggle" onClick={() => setNotesOpen(!notesOpen)}>
          <span className={`gsa-bulletin-chevron ${notesOpen ? 'open' : ''}`}>&#9662;</span>
          Version Notes
        </button>
        {notesOpen && (
          <div className="gsa-bulletin-body">
            {VERSION_NOTES.map(({ version, note }) => (
              <div key={version} className="gsa-bulletin-item">
                <span className="gsa-bulletin-version">{version}</span>
                <span className="gsa-bulletin-note">{note}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="gsa-error">{error}</div>}

      {!data && !loading && !error && (
        <div className="gsa-empty">Select a date range and click Run Report</div>
      )}

      {loading && (
        <div className="gsa-loading">
          <div className="gsa-spinner" />
          <p>Querying Analytics...</p>
        </div>
      )}

      {data && !loading && (
        <div className="gsa-content">
          {(installBaseChartData.length > 0 || uniqueDaysChartData.length > 0) && (
            <div className="gsa-chart-section">
              <h3 className="gsa-section-title">Adoption</h3>
              <div className="gsa-adoption-charts">
                {installBaseChartData.length > 0 && (
                  <div className="gsa-adoption-chart">
                    <h4 className="gsa-section-subtitle">Install Base</h4>
                    <div className="gsa-chart-container">
                      <ResponsiveBar
                        data={installBaseChartData}
                        keys={installBaseKeys}
                        indexBy="sw_version"
                        theme={chartTheme}
                        onClick={(bar) => setSelectedVersion(
                          selectedVersion === bar.indexValue ? null : bar.indexValue
                        )}
                        margin={{ top: 20, right: 140, bottom: 60, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={INSTALL_BASE_COLORS}
                        borderRadius={2}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 5, tickPadding: 5, tickRotation: -45,
                          legend: 'Software Version', legendOffset: 50, legendPosition: 'middle'
                        }}
                        axisLeft={{
                          tickSize: 5, tickPadding: 5, tickRotation: 0,
                          legend: 'Unique Users', legendOffset: -50, legendPosition: 'middle'
                        }}
                        enableGridY={true}
                        enableLabel={false}
                        legends={[{
                          dataFrom: 'keys', anchor: 'right', direction: 'column',
                          translateX: 120, itemsSpacing: 4, itemWidth: 100, itemHeight: 18,
                          itemTextColor: '#999999', itemDirection: 'left-to-right',
                          symbolSize: 12, symbolShape: 'square'
                        }]}
                      />
                    </div>
                  </div>
                )}
                {uniqueDaysChartData.length > 0 && (
                  <div className="gsa-adoption-chart">
                    <h4 className="gsa-section-subtitle"># of Unique Days</h4>
                    <div className="gsa-chart-container">
                      <ResponsiveBar
                        data={uniqueDaysChartData}
                        keys={uniqueDaysKeys}
                        indexBy="sw_version"
                        theme={chartTheme}
                        onClick={(bar) => setSelectedVersion(
                          selectedVersion === bar.indexValue ? null : bar.indexValue
                        )}
                        margin={{ top: 20, right: 140, bottom: 60, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={INSTALL_BASE_COLORS}
                        borderRadius={2}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                          tickSize: 5, tickPadding: 5, tickRotation: -45,
                          legend: 'Software Version', legendOffset: 50, legendPosition: 'middle'
                        }}
                        axisLeft={{
                          tickSize: 5, tickPadding: 5, tickRotation: 0,
                          legend: 'Unique Days', legendOffset: -50, legendPosition: 'middle'
                        }}
                        enableGridY={true}
                        enableLabel={false}
                        legends={[{
                          dataFrom: 'keys', anchor: 'right', direction: 'column',
                          translateX: 120, itemsSpacing: 4, itemWidth: 100, itemHeight: 18,
                          itemTextColor: '#999999', itemDirection: 'left-to-right',
                          symbolSize: 12, symbolShape: 'square'
                        }]}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {versionSummary.length > 0 && (
            <div className="gsa-version-users">
              <h3 className="gsa-section-title">Unique Days by Version</h3>
              <div className="gsa-version-users-layout">
                <div className="gsa-version-table">
                  <div className="gsa-table-header gsa-table-header-versions">
                    <span>Version</span>
                    <span>Users</span>
                    <span>Days</span>
                  </div>
                  <div className="gsa-table-scroll">
                    {versionSummary.map(row => (
                      <button
                        key={row.sw_version}
                        type="button"
                        className={`gsa-table-row gsa-table-row-versions ${selectedVersion === row.sw_version ? 'selected' : ''}`}
                        onClick={() => setSelectedVersion(
                          selectedVersion === row.sw_version ? null : row.sw_version
                        )}
                      >
                        <span className="gsa-table-cell-primary">{row.sw_version}</span>
                        <span>{row.user_count.toLocaleString()}</span>
                        <span>{row.total_days.toLocaleString()}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="gsa-users-table">
                  <div className="gsa-table-header gsa-table-header-users">
                    <span>
                      {selectedVersion ? `Users on ${selectedVersion}` : 'User'}
                    </span>
                    <span>Days</span>
                    <span></span>
                  </div>
                  <div className="gsa-table-scroll">
                    {selectedVersion ? (
                      usersForVersion.length === 0 ? (
                        <div className="gsa-users-empty">No users on this version</div>
                      ) : (
                        usersForVersion.map(user => (
                          <a
                            key={user.user_identifier}
                            {...(user.user_id && { href: `/users/${user.user_id}` })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`gsa-table-row gsa-table-row-users ${user.user_id ? 'gsa-table-row-clickable' : ''}`}
                            onClick={(e) => handleUserClick(e, user)}
                          >
                            <span className="gsa-table-cell-primary gsa-user-name-cell">
                              {user.user_name || user.user_id || user.user_identifier.substring(0, 12) + '...'}
                            </span>
                            <span>{user.unique_days.toLocaleString()}</span>
                            <span className="gsa-copy-cell">
                              {user.user_id && (
                                <button
                                  className="gsa-copy-btn"
                                  title="Copy user ID"
                                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(user.user_id); }}
                                >
                                  &#x1F4CB;
                                </button>
                              )}
                            </span>
                          </a>
                        ))
                      )
                    ) : (
                      <div className="gsa-users-empty">Select a version to see users</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GogglesSWAdoptionReport;
